import { foldDiacritics, mod } from "@/lib/tool-kit/ciphers/alphabet";

/**
 * Key squares for the Polybius-based ciphers (four-square, bifid, ADFGX/ADFGVX, Nihilist).
 *
 * A square is a string, row by row, `size × size` characters long. Three conventions
 * decide what goes in it, and each is selectable in the widgets because published
 * examples differ:
 *
 * - **What fits.** 25 cells cannot hold 26 letters: either I and J share a cell
 *   (`merge-ij`, the usual choice) or Q is left out (`drop-q`). A 6 × 6 square (`alnum`)
 *   holds A–Z and 0–9 and needs neither.
 * - **How the keyword is written in.** A keyword's letters go in first, each once, then
 *   the rest of the alphabet in order. They can be written along the rows (the usual
 *   way), down the columns (the American Cryptogram Association's "vertical route"), or
 *   in a clockwise spiral from the top-left corner (the ACA's bifid example).
 */

export type GridAlphabet = "merge-ij" | "drop-q" | "alnum";
export type SquareFill = "rows" | "columns" | "spiral";

const ALPHABETS: Record<GridAlphabet, string> = {
  "merge-ij": "ABCDEFGHIKLMNOPQRSTUVWXYZ",
  "drop-q": "ABCDEFGHIJKLMNOPRSTUVWXYZ",
  alnum: "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789",
};

/** The characters a square of this kind holds, in plain order. */
export function gridAlphabet(kind: GridAlphabet): string {
  return ALPHABETS[kind];
}

/** Side length of the square: 5, or 6 for A–Z and 0–9. */
export function gridSize(kind: GridAlphabet): number {
  return kind === "alnum" ? 6 : 5;
}

/**
 * A character as the square knows it: capitals, accents folded, J → I when I and J
 * share a cell. "" for a character the square does not hold (Q in a square without Q,
 * punctuation, spaces, letters of other alphabets).
 */
export function gridChar(char: string, kind: GridAlphabet): string {
  const upper = foldDiacritics(char).toUpperCase();
  const mapped = kind === "merge-ij" && upper === "J" ? "I" : upper;
  return mapped.length === 1 && ALPHABETS[kind].includes(mapped) ? mapped : "";
}

/** The characters of a text that the square holds, and how many Qs a square without Q dropped. */
export function gridText(
  text: string,
  kind: GridAlphabet
): { chars: string; droppedQ: number } {
  let chars = "";
  let droppedQ = 0;
  for (const char of foldDiacritics(text)) {
    const mapped = gridChar(char, kind);
    if (mapped !== "") {
      chars += mapped;
    } else if (kind === "drop-q" && (char === "Q" || char === "q")) {
      droppedQ += 1;
    }
  }
  return { chars, droppedQ };
}

/** A keyword's characters then the rest of the alphabet, each character once. */
export function squareSequence(keyword: string, kind: GridAlphabet): string {
  const used = new Set<string>();
  let sequence = "";
  for (const char of `${keyword}${ALPHABETS[kind]}`) {
    const mapped = gridChar(char, kind);
    if (mapped !== "" && !used.has(mapped)) {
      used.add(mapped);
      sequence += mapped;
    }
  }
  return sequence;
}

function spiralOrder(size: number): number[] {
  const order: number[] = [];
  let top = 0;
  let bottom = size - 1;
  let left = 0;
  let right = size - 1;
  while (top <= bottom && left <= right) {
    for (let column = left; column <= right; column += 1) {
      order.push(top * size + column);
    }
    for (let row = top + 1; row <= bottom; row += 1) {
      order.push(row * size + right);
    }
    if (top < bottom) {
      for (let column = right - 1; column >= left; column -= 1) {
        order.push(bottom * size + column);
      }
    }
    if (left < right) {
      for (let row = bottom - 1; row > top; row -= 1) {
        order.push(row * size + left);
      }
    }
    top += 1;
    bottom -= 1;
    left += 1;
    right -= 1;
  }
  return order;
}

/** Cell indexes (row-major) in the order a fill route visits them. */
export function fillOrder(size: number, fill: SquareFill): number[] {
  if (fill === "spiral") {
    return spiralOrder(size);
  }
  const order: number[] = [];
  for (let place = 0; place < size * size; place += 1) {
    const row = fill === "rows" ? Math.floor(place / size) : place % size;
    const column = fill === "rows" ? place % size : Math.floor(place / size);
    order.push(row * size + column);
  }
  return order;
}

/** The key square for a keyword: its characters written in by `fill`, row by row. */
export function buildSquare(
  keyword: string,
  kind: GridAlphabet,
  fill: SquareFill
): string {
  const size = gridSize(kind);
  const sequence = squareSequence(keyword, kind);
  const cells: string[] = Array.from({ length: size * size }, () => "");
  for (const [place, cell] of fillOrder(size, fill).entries()) {
    cells[cell] = sequence.charAt(place);
  }
  return cells.join("");
}

/** Row and column (0-based) of a character in a square. */
export function squarePoint(
  square: string,
  size: number,
  char: string
): { row: number; column: number } {
  const index = square.indexOf(char);
  return { row: Math.floor(index / size), column: mod(index, size) };
}
